import type { Resident } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { useCallback } from 'react'

import { useQuery, useQueryClient } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import {
  RESIDENT_DATA_KEY,
  RESIDENTS_QUERY_KEY,
  RESIDENTS_URL
} from './queryConstants'

type UseResidentsListResponse = {
  residents: Resident[]
  isLoading: boolean
  refetchResidents: () => void
}

type UseResidentsList = {
  filters?: Record<string, any>
}

const useResidentsList = ({
  filters
}: UseResidentsList): UseResidentsListResponse => {
  const queryClient = useQueryClient()

  const onSuccess = useCallback((data: { result: Resident[] }) => {
    const residents = data.result

    residents?.forEach?.((resident) => {
      queryClient.setQueryData([RESIDENT_DATA_KEY, resident.id], {
        result: resident
      })
    })
  }, [])

  const params = getQueryParams({
    fields: [
      'id',
      'address',
      'addresses',
      'name',
      'picture',
      'status',
      'gender',
      'location',
      'carePackage',
      'dnacpr',
      'totalResidentMinutes'
    ],
    ...filters
  })

  const {
    isLoading: isLoadingResidents,
    data: residentsData,
    refetch
  } = useQuery(
    [RESIDENTS_QUERY_KEY, params],
    () =>
      getJSON({
        url: `${RESIDENTS_URL}?${params}`,
        isRota: true
      }),
    {
      onSuccess,
      retry: 1
    }
  )

  return {
    residents: (residentsData || []) as Resident[],
    isLoading: isLoadingResidents,
    refetchResidents: refetch
  }
}

export default useResidentsList
